import React from 'react';
import { ArrowUpRightIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { programs } from '../../data/programs';
import { site } from '../../data/site';
import { usePrograms } from '../../contexts/ProgramContext';
import type { TrialChoice } from '../../types/content';
import { Container } from '../ui/Container';
import { Reveal } from '../ui/Reveal';
import { SectionTitle } from '../ui/SectionTitle';
const choices: {
  id: TrialChoice;
  label: string;
}[] = [...programs.filter(p => p.id !== 'competition-team').map(p => ({
  id: p.id as TrialChoice,
  label: p.name
})), {
  id: 'not-sure',
  label: 'Not sure yet'
}];
export function FirstClass() {
  const {
    trialChoice,
    setTrialChoice
  } = usePrograms();
  const selected = choices.find(c => c.id === trialChoice) ?? choices[choices.length - 1];
  const programLabel = selected.id === 'not-sure' ? 'a program (not sure which yet)' : selected.label;
  const subject = `Free trial class: ${selected.id === 'not-sure' ? 'help choosing a program' : selected.label}`;
  const body = `Hi Bates Taekwondo,\n\nI'd like to book a free trial class for ${programLabel}.\n\nName:\nStudent's age:\nBest way to reach me:\n\nThank you!`;
  const mailHref = `${site.emailHref}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return <section id="first-class" aria-labelledby="first-class-title" className="bg-white py-20 lg:py-28" data-id="element-144" data-mp="2ebedq5aku45h">
      <Container data-id="element-145" data-mp="1gqi9smcf93g0">
        <Reveal className="grid gap-6 lg:grid-cols-12 lg:items-end" data-id="element-146" data-mp="1gr1ugatgi5xq">
          <SectionTitle id="first-class-title" className="lg:col-span-7" data-id="element-147" data-mp="1lotk0y6mpfyr">
            Your free trial class in three steps
          </SectionTitle>
          <p className="text-pretty text-[1.05rem] leading-relaxed text-muted lg:col-span-5 lg:pb-2" data-id="element-148" data-mp="1uoqe5fbsf717">
            Your first class is free. Pick a program, reach out however suits you, and come see what training at Bates
            feels like.
          </p>
        </Reveal>

        <ol className="mt-14 border-b border-line" data-id="element-149" data-mp="3622e3f4ff5i3">
          <li className="grid gap-6 border-t border-line py-10 lg:grid-cols-12" data-id="element-150" data-mp="16ycb8652q1jx">
            <StepHeading number="1" title="Choose a program" className="lg:col-span-4" data-id="element-151" data-mp="120apfdg9jo4r" />
            <div className="lg:col-span-8" data-id="element-152" data-mp="1fztkp5nz74zn">
              <fieldset data-id="element-153" data-mp="1fyikfftqofda">
                <legend className="text-[15px] text-muted" data-id="element-154" data-mp="1fz0m4rz67lsl">Who is the class for? The school can help if you’re unsure.</legend>
                <div className="mt-4 flex flex-wrap gap-2.5" data-id="element-155" data-mp="nb4kd8uqv3oy">
                  {choices.map(choice => {
                  const isOn = choice.id === trialChoice;
                  return <label key={choice.id} className={`relative inline-flex min-h-[48px] cursor-pointer items-center rounded border-2 px-5 text-[15px] font-semibold transition-colors duration-150 has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-offset-[3px] has-[:focus-visible]:outline-brand ${isOn ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink hover:border-ink/40'}`} data-id="element-156" data-mp="x0xwcaystktq">
                        <input type="radio" name="trial-program" value={choice.id} checked={isOn} onChange={() => setTrialChoice(choice.id)} className="sr-only" data-id="element-157" data-mp="2g0x2yxtr3109" />
                        {choice.label}
                      </label>;
                })}
                </div>
              </fieldset>
            </div>
          </li>

          <li className="grid gap-6 border-t border-line py-10 lg:grid-cols-12" data-id="element-158" data-mp="2pxtdf937lx7g">
            <StepHeading number="2" title="Contact the school" className="lg:col-span-4" data-id="element-159" data-mp="2pzwceimcb0ob" />
            <div className="lg:col-span-8" data-id="element-160" data-mp="19i8mgih9d6r3">
              <p className="text-[15px] text-muted" data-id="element-161" data-mp="1ej632qmmdsqh">Choose whichever is easiest for you.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-3" data-id="element-162" data-mp="1ts1g8208m633">
                <a href={site.trialFormUrl} target="_blank" rel="noopener noreferrer" className="group flex min-h-[132px] flex-col justify-between rounded bg-brand p-5 text-white transition-colors duration-150 hover:bg-brand-dark" data-id="element-163" data-mp="1tue5z1c8lkqp">
                  <ArrowUpRightIcon className="h-6 w-6 transition-transform duration-150 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-164" data-mp="2cr5k6wcx99nw" />
                  <span data-id="element-165" data-mp="23po5nif5xg1b">
                    <span className="block font-display text-[1.45rem] font-bold uppercase leading-tight" data-id="element-166" data-mp="23owcee91fue7">Request a trial online</span>
                    <span className="mt-1 block text-sm text-white/85" data-id="element-167" data-mp="sbanmu73but7">Opens the Bates trial request page</span>
                  </span>
                  <span className="sr-only" data-id="element-168" data-mp="nbxz850kmfoi"> (opens in a new tab)</span>
                </a>
                <a href={site.phoneHref} className="group flex min-h-[132px] flex-col justify-between rounded border-2 border-line p-5 transition-colors duration-150 hover:border-ink" data-id="element-169" data-mp="d9264f35zd80">
                  <PhoneIcon className="h-6 w-6 text-brand" aria-hidden="true" data-id="element-170" data-mp="n55j6fzk0w9q" />
                  <span data-id="element-171" data-mp="rxyph8ao8cnx">
                    <span className="block font-display text-[1.45rem] font-bold uppercase leading-tight" data-id="element-172" data-mp="rz9rp3ogxccx">Call the school</span>
                    <span className="mt-1 block text-sm text-muted" data-id="element-173" data-mp="10xv92vi7elc4">{site.phoneDisplay}</span>
                  </span>
                </a>
                <a href={mailHref} className="group flex min-h-[132px] flex-col justify-between rounded border-2 border-line p-5 transition-colors duration-150 hover:border-ink" data-id="element-174" data-mp="15xzpymn0xi2v">
                  <MailIcon className="h-6 w-6 text-brand" aria-hidden="true" data-id="element-175" data-mp="37qe4dbqd04ej" />
                  <span data-id="element-176" data-mp="3cnxsr4grtxk7">
                    <span className="block font-display text-[1.45rem] font-bold uppercase leading-tight" data-id="element-177" data-mp="3cpieq5p0s86g">Send an email</span>
                    <span className="mt-1 block text-sm text-muted" data-id="element-178" data-mp="2znvdjmvfgsan">{site.email}</span>
                  </span>
                </a>
              </div>
              <p className="mt-4 text-sm text-muted" aria-live="polite" data-id="element-179" data-mp="3emh0li7rca81">
                Your email will start with{' '}
                <span className="font-semibold text-ink" data-id="element-180" data-mp="39nvx99x7lafd">
                  {selected.id === 'not-sure' ? 'a request for help choosing a program' : selected.label}
                </span>{' '}
                filled in.
              </p>
            </div>
          </li>

          <li className="grid gap-6 border-t border-line py-10 lg:grid-cols-12" data-id="element-181" data-mp="14oinz18ybwo">
            <StepHeading number="3" title="Come try a class" className="lg:col-span-4" data-id="element-182" data-mp="3sdc229fxfp1l" />
            <div className="flex flex-col gap-5 lg:col-span-8 md:flex-row md:items-center md:justify-between" data-id="element-183" data-mp="a5wdu88guwmj">
              <p className="text-pretty max-w-[34rem] text-[1.05rem] leading-relaxed" data-id="element-184" data-mp="663ztg0azy9s">
                Visit us at {site.street}. Classes run on weekdays, and the school will point you to the class that fits
                your age and experience.
              </p>
              <a href={site.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] shrink-0 items-center gap-2 text-[15px] font-semibold underline decoration-brand decoration-2 underline-offset-4 hover:text-brand" data-id="element-185" data-mp="fsyjqag8d8af">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-186" data-mp="aun8bvaaepq3" />
                Get directions
                <span className="sr-only" data-id="element-187" data-mp="3t8mffvkz5kwp"> (opens in a new tab)</span>
              </a>
            </div>
          </li>
        </ol>
      </Container>
    </section>;
}
function StepHeading({
  number,
  title,
  className = ''
}: {
  number: string;
  title: string;
  className?: string;
}) {
  return <div className={`flex items-baseline gap-4 ${className}`} data-id="element-188" data-mp="3o7gljufkvrll">
      <span className="font-display text-[2.75rem] font-bold leading-none text-brand" aria-hidden="true" data-id="element-189" data-mp="7l56krwizv7a">
        {number}
      </span>
      <h3 className="font-display text-[1.9rem] font-bold uppercase leading-none" data-id="element-190" data-mp="2n3pgcinu5yg">
        <span className="sr-only" data-id="element-191" data-mp="3upbleadf31m0">Step {number}: </span>
        {title}
      </h3>
    </div>;
}